import { CursorCoordinates, CursorData, PANEL_TOOLTIP_PADDING } from './model';

/**
 * Determine position of tooltip depending on chart dimensions and portal container.
 */
export function assembleTransform({
  mousePos,
  pinnedPos,
  tooltipHeight,
  tooltipWidth,
  containerElement,
}: {
  mousePos: CursorData['coords'];
  pinnedPos: CursorCoordinates | null | undefined;
  tooltipHeight: number;
  tooltipWidth: number;
  containerElement?: Element | null;
}) {
  if (!mousePos) return undefined;

  const cursorPaddingX = 32;
  const cursorPaddingY = 16;

  if (pinnedPos) {
    mousePos = pinnedPos;
  }

  // plotCanvas corresponds to zrX and zrY, which can be undefined when not hovering over a chart canvas.
  if (mousePos.plotCanvas.x === undefined || mousePos.plotCanvas.y === undefined) return undefined;

  // By default, tooltip is located in a Portal attached to the body.
  // Using page coordinates instead of viewport ensures the tooltip is
  // absolutely positioned correctly as the user scrolls.
  let x = mousePos.page.x;
  let y = mousePos.page.y + cursorPaddingY;

  // If containerElement is defined, tooltip is attached to the containerElement instead.
  let containerRect;
  if (containerElement) {
    // Get the container's position relative to viewport.
    containerRect = containerElement.getBoundingClientRect();
    // Calculate the mouse position relative to container.
    x = x - containerRect.left + containerElement.scrollLeft;
    y = y - containerRect.top + containerElement.scrollTop;
  }

  const willOverflowBottom = mousePos.client.y + tooltipHeight + cursorPaddingY > window.innerHeight - cursorPaddingY;
  if (willOverflowBottom) {
    // Adjust so tooltip does not get cut off at bottom of chart while also making sure it does not cut off at the
    // top of the viewport
    y = Math.max(PANEL_TOOLTIP_PADDING, y - tooltipHeight - cursorPaddingY * 2);
  }

  // Determine if the tooltip will fit on the right or not. Calculating the overflow, so we can use it to adjust the
  // alignment of the tooltip if it will not fit in either of the standard right/left positions.
  const rightOverflow = containerRect
    ? containerRect.width - PANEL_TOOLTIP_PADDING - (x + cursorPaddingX + tooltipWidth)
    : window.innerWidth - PANEL_TOOLTIP_PADDING - (mousePos.client.x + cursorPaddingX + tooltipWidth);

  // Decide whether to flip the tooltip to the left side of the cursor.
  // Only flip when the tooltip would overflow the right edge of the container/viewport.
  const willOverflowRight = rightOverflow < 0;

  // Double-check if the tooltip will fit on the left if we flip to that direction.
  const willOverflowLeft = containerRect
    ? x - cursorPaddingX - tooltipWidth < 0 + PANEL_TOOLTIP_PADDING
    : mousePos.client.x - cursorPaddingX - tooltipWidth < 0 + PANEL_TOOLTIP_PADDING;

  // Tooltip does not fit in the standard right position.
  if (willOverflowRight) {
    if (!willOverflowLeft) {
      // The tooltip will fit in the standard left position. Switch to that position.
      return `translate3d(${x - cursorPaddingX}px, ${y}px, 0) translateX(-100%)`;
    }

    // The tooltip will not fit in either of the standard left/right positions. Adjust the position of the tooltip to
    // attempt to fit in the screen. Worst case, it will position it near the left of the container with a little
    // padding).
    return `translate3d(${Math.max(PANEL_TOOLTIP_PADDING, x - cursorPaddingX + rightOverflow)}px, ${y}px, 0)`;
  }

  // Tooltip will fit in the standard right position.
  return `translate3d(${x + cursorPaddingX}px, ${y}px, 0)`;
}
